import { inject, Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserInfoService } from '../services/user-info.service';
import { catchError, map, tap } from 'rxjs/operators';
import { UserInfoHeader } from '../models/user-info.model';
import { UserStateService } from '../services/user-state.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private userInfoService = inject(UserInfoService);
  private userStateService = inject(UserStateService);
  canActivate(): Observable<boolean | UrlTree> {
    const token = this.authService.getAccessToken();
    if (!token) {
      this.authService.handleRedirect();
      return of(false);
    }

    const userId = localStorage.getItem('userId');
    if (userId) {
      return this.userInfoService.getFullNamesRFC({ iv_user: userId.toUpperCase() }).pipe(
        tap((res) =>
          this.userInfoService.userInfo.set(this.mapToHeader(res.es_userinfo))),
        map(() => true),
        catchError(() =>
          this.userInfoService.getUserInfo().pipe(
            tap((userInfo) => {
              if (userInfo?.sub) {
                this.userInfoService.userInfo.set(this.mapToHeader(userInfo));
                localStorage.setItem('userId', userInfo.sub.toUpperCase());
              }
            }),
            map((userInfo) => !!userInfo && !!userInfo.sub),
            catchError(() => of(true)), // Allow route even if display info fails
          ),
        ),
      );
    }
    return this.userInfoService.getUserInfo().pipe(
      tap((userInfo) => {
        if (userInfo?.sub) {
          localStorage.setItem('userId', userInfo.sub.toUpperCase());
          this.userInfoService.userInfo.set(this.mapToHeader(userInfo));
        }
      }),
      map((userInfo) => !!userInfo && !!userInfo.sub),
      catchError(() => {
        // Token invalid or expired: clear storage and redirect
        localStorage.removeItem('userId');
        this.authService.handleRedirect();
        return of(false);
      })
    );
  }

  private mapToHeader(user: any): UserInfoHeader {
    const fullName = `${user.name1 || ''} ${user.name2 || ''}`.trim();
    localStorage.setItem('employeeName', fullName);
    this.userStateService.setEmployeeName(fullName);
    return {
      username: fullName || '',
      department: user.department || '',
      profileImg: user.profileImg || '',
    };
  }
}
